import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useRecords } from '../features/records/api';
import { useSession } from '../auth/useSession';
import { NewRecordForm } from '../features/records/NewRecordForm';
import NavigationHeader from '../components/ui/NavigationHeader';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Icon from '../components/AppIcon';

export default function Dashboard() {
  const { user, signOut } = useSession();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showNewForm, setShowNewForm] = useState(false);

  const { data: recordsData, isLoading, error } = useRecords({
    page: currentPage,
    pageSize: 10,
    q: searchQuery,
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified':
        return 'text-success bg-success/10';
      case 'submitted':
        return 'text-warning bg-warning/10';
      case 'rejected':
        return 'text-destructive bg-destructive/10';
      default:
        return 'text-muted-foreground bg-muted/50';
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader />
      
      <main className="pt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-6xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
                <p className="text-muted-foreground">
                  Welcome back, {user?.email}
                </p>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowNewForm(!showNewForm)}
                  iconName={showNewForm ? "X" : "Plus"}
                  iconPosition="left"
                >
                  {showNewForm ? 'Cancel' : 'New Record'}
                </Button>
                <Button
                  variant="outline"
                  onClick={signOut}
                  iconName="LogOut"
                  iconPosition="left"
                >
                  Sign Out
                </Button>
              </div>
            </div>

            {/* New Record Form */}
            {showNewForm && (
              <NewRecordForm onSuccess={() => setShowNewForm(false)} />
            )}

            {/* Search */}
            <div className="bg-card border border-border rounded-lg p-6">
              <form onSubmit={handleSearch} className="flex space-x-4">
                <div className="flex-1">
                  <Input
                    placeholder="Search records by title or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Button type="submit" iconName="Search">
                  Search
                </Button>
              </form>
            </div>

            {/* Records List */}
            <div className="bg-card border border-border rounded-lg">
              <div className="p-6 border-b border-border">
                <h2 className="text-lg font-semibold text-foreground">
                  Your Records ({recordsData?.count || 0})
                </h2>
              </div>

              {isLoading ? (
                <div className="p-8 text-center">
                  <Icon name="Loader2" size={32} className="text-primary animate-spin mx-auto mb-4" />
                  <p className="text-muted-foreground">Loading records...</p>
                </div>
              ) : error ? (
                <div className="p-8 text-center">
                  <Icon name="AlertCircle" size={32} className="text-destructive mx-auto mb-4" />
                  <p className="text-destructive">Failed to load records</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {error.message}
                  </p>
                </div>
              ) : recordsData?.data.length === 0 ? (
                <div className="p-8 text-center">
                  <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    No records found
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    {searchQuery 
                      ? 'No records match your search criteria.' 
                      : 'Create your first notarized record to get started.'
                    }
                  </p>
                  {!searchQuery && (
                    <Button
                      onClick={() => setShowNewForm(true)}
                      iconName="Plus"
                      iconPosition="left"
                    >
                      Create First Record
                    </Button>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {recordsData?.data.map((record) => (
                    <div key={record.id} className="p-6 hover:bg-muted/30 transition-smooth">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3 mb-2">
                            <Link
                              to={`/records/${record.id}`}
                              className="text-lg font-semibold text-foreground hover:text-primary transition-smooth"
                            >
                              {record.title}
                            </Link>
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(record.status)}`}
                            >
                              {record.status}
                            </span>
                          </div>
                          
                          {record.description && (
                            <p className="text-muted-foreground mb-2 line-clamp-2">
                              {record.description}
                            </p>
                          )}
                          
                          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                            <div className="flex items-center space-x-1">
                              <Icon name="Calendar" size={14} />
                              <span>{format(new Date(record.created_at), 'MMM d, yyyy')}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <Icon name="Hash" size={14} />
                              <span className="font-mono">
                                {record.content_hash.slice(0, 8)}...
                              </span>
                            </div>
                            {record.tags.length > 0 && (
                              <div className="flex items-center space-x-1">
                                <Icon name="Tag" size={14} />
                                <span>{record.tags.slice(0, 2).join(', ')}</span>
                                {record.tags.length > 2 && (
                                  <span>+{record.tags.length - 2}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <Link to={`/records/${record.id}`}>
                          <Button variant="outline" size="sm" iconName="ArrowRight">
                            View
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {recordsData && recordsData.totalPages > 1 && (
                <div className="p-6 border-t border-border">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      Page {currentPage} of {recordsData.totalPages}
                    </p>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        iconName="ChevronLeft"
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(recordsData.totalPages, p + 1))}
                        disabled={currentPage === recordsData.totalPages}
                        iconName="ChevronRight"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}